import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Globals from "../../utils/globals";
import "./IncidentResolution.css";

const IncidentResolution = ({
    incident,
    agency,
    reportSchemaId,
    languageCode,
    profile,
    onResolutionSaved,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const openResolution = () => {

        setIsOpen(true);
        setSaveError("");

        console.log(
            "[IncidentResolution] saved resolution",
            incident?.resolution
        );

        loadClearanceSchema();
        loadSavedResolution();
    };

    const closeResolution = () => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }

        setIsOpen(false);
    };

    const loadSavedResolutionValues = () => {
        const savedValues = Array.isArray(incident?.resolution?.values)
            ? incident.resolution.values
            : [];

        const restoredValues = {};

        savedValues.forEach((entry) => {
            if (!entry?.item_id) return;

            if (entry.type === "list") {
                restoredValues[entry.item_id] = entry.option_id || "";
                return;
            }

            restoredValues[entry.item_id] = entry.value;
        });

        setResolutionValues(restoredValues);
    };

    const loadSavedResolution = async () => {

        const agencyId = agency?.agencyId;

        if (!incident?._id || !agencyId || !reportSchemaId) {
            return;
        }

        try {
            const token = await getAccessTokenSilently();

            const url =
                `${Globals.apiEndpoint}/resolutions/agencies/${agencyId}` +
                `/report_schemas/${reportSchemaId}` +
                `/reports/${incident._id}`;

            console.log("[IncidentResolution] incident id:", incident?._id);
            console.log("[IncidentResolution] report schema id:", reportSchemaId);
            console.log("[IncidentResolution] saved resolution URL:", url);

            const resp = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const json = await resp.json().catch(() => ({}));

            if (!resp.ok) {
                console.error(
                    "[IncidentResolution] failed to load saved resolution:",
                    json?.message || resp.status
                );
                return;
            }

            const savedValues = Array.isArray(json?.resolution?.values)
                ? json.resolution.values
                : [];

            const restoredValues = {};

            savedValues.forEach((entry) => {
                if (!entry?.item_id) return;

                restoredValues[entry.item_id] =
                    entry.type === "list"
                        ? entry.option_id || ""
                        : entry.value;
            });

            setResolutionValues(restoredValues);
        } catch (error) {
            console.error(
                "[IncidentResolution] saved resolution load failed:",
                error
            );
        }
    };

    const loadClearanceSchema = async () => {
        const agencyId = agency?.agencyId;

        if (!agencyId) {
            setClearanceError("Missing agency information.");
            return;
        }

        setIsLoadingClearance(true);
        setClearanceError("");

        try {
            const token = await getAccessTokenSilently();

            const url =
                `${Globals.apiEndpoint}/resolutions/agencies/${agencyId}` +
                `/clearance_schema`;

            const resp = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const json = await resp.json().catch(() => ({}));

            if (!resp.ok) {
                setClearanceError(
                    json?.message || "Failed to load resolution actions."
                );
                return;
            }

            setClearanceSchema(json?.resolution_schema || null);
        } catch (error) {
            console.error("[IncidentResolution] clearance load failed:", error);
            setClearanceError("Failed to load resolution actions.");
        } finally {
            setIsLoadingClearance(false);
        }
    };

    const updateResolutionValue = (itemId, value) => {
        setResolutionValues((current) => ({
            ...current,
            [itemId]: value,
        }));
    };


    const createResolutionKey = (title = "") =>
        title
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "");


    const saveResolution = async () => {
        console.log("=== saveResolution() started ===");
        const agencyId = agency?.agencyId;
        const schemaItems = Array.isArray(clearanceSchema?.items)
            ? clearanceSchema.items
            : [];

        if (!incident?._id || !agencyId || !reportSchemaId) {
            setSaveError("Missing incident, agency, or report schema information.");
            return;
        }

        if (!clearanceSchema?._id) {
            setSaveError("The resolution form schema has not loaded.");
            return;
        }

        // This list field is the primary classification for the resolved incident.
        const resolvedTypeItem = schemaItems.find(
            (item) =>
                item.type === "list" &&
                item.statistic_name?.trim().toLowerCase() ===
                "resolution type"
        );

        if (!resolvedTypeItem) {
            setSaveError(
                'The resolution form is missing the "Resolved Incident Type" field.'
            );
            return;
        }

        const selectedResolvedOptionId =
            resolutionValues[resolvedTypeItem._id] || "";

        const selectedResolvedOption = (
            resolvedTypeItem.items || []
        ).find(
            (option) =>
                String(option._id) === String(selectedResolvedOptionId)
        );

        if (!selectedResolvedOption) {
            setSaveError("Please select a Resolved Incident Type.");
            return;
        }

        const values = schemaItems
            .filter((item) => item.type !== "photo")
            .map((item) => {
                const title = item.title?.en_US || "Untitled field";
                const enteredValue = resolutionValues[item._id];

                if (item.type === "list") {
                    const selectedOption = (item.items || []).find(
                        (option) =>
                            String(option._id) === String(enteredValue)
                    );

                    if (!selectedOption) return null;

                    return {
                        item_id: String(item._id),
                        key: createResolutionKey(title),
                        title,
                        statistic_name: item.statistic_name || null,
                        type: item.type,
                        option_id: String(selectedOption._id),
                        option_title:
                            selectedOption.title?.en_US || "Untitled option",
                        value:
                            selectedOption.title?.en_US || "Untitled option",
                        should_be_aggregated: false,
                    };
                }

                if (item.type === "bool") {
                    return {
                        item_id: String(item._id),
                        key: createResolutionKey(title),
                        title,
                        statistic_name: item.statistic_name || null,
                        type: item.type,
                        option_id: null,
                        option_title: null,
                        value: enteredValue === true,
                        should_be_aggregated: false,
                    };
                }

                // Do not save untouched blank text or number fields.
                if (
                    enteredValue === undefined ||
                    enteredValue === null ||
                    enteredValue === ""
                ) {
                    return null;
                }

                return {
                    item_id: String(item._id),
                    key: createResolutionKey(title),
                    title,
                    statistic_name: item.statistic_name || null,
                    type: item.type,
                    option_id: null,
                    option_title: null,
                    value: enteredValue,
                    should_be_aggregated:
                        item.should_be_aggregated === true,
                };
            })
            .filter(Boolean);

        setIsSaving(true);
        setSaveError("");

        try {
            const token = await getAccessTokenSilently();

            const url =
                `${Globals.apiEndpoint}/resolutions/agencies/${agencyId}` +
                `/report_schemas/${reportSchemaId}` +
                `/reports/${incident._id}`;

            const currentAssignment = Array.isArray(incident?.assignments)
                ? incident.assignments.find(
                    (assignment) => assignment?.current === true
                )
                : null;

            const handledBy = {
                department_id:
                    currentAssignment?.assignee?.department_id || null,
                department_name:
                    currentAssignment?.assignee?.department_name || null,

                role_id:
                    currentAssignment?.assignee?.role_id || null,
                role_name:
                    currentAssignment?.assignee?.role_name || null,

                shift_id:
                    currentAssignment?.assignee?.shift_id || null,
                shift_name:
                    currentAssignment?.assignee?.shift_name || null,

                unit_id:
                    currentAssignment?.assignee?.unit_id || null,
                unit_name:
                    currentAssignment?.assignee?.unit_name || null,
            };
            const payload = {
                schema_id: String(clearanceSchema._id),
                schema_title:
                    clearanceSchema.title?.en_US || "Clearance Report",

                resolved_incident_type: {
                    item_id: String(resolvedTypeItem._id),
                    option_id: String(selectedResolvedOption._id),
                    value:
                        selectedResolvedOption.title?.en_US ||
                        "Untitled option",
                },

                values,

                completed_by: {
                    name: profile?.name || profile?.details?.name || null,
                    email: profile?.email || profile?.details?.email || null,
                },

                completed_at: Date.now(),
                source: "console",

                handled_by: handledBy,

                coordinate: incident.coordinate || {},
                coordinate_error: incident.coordinate_error || null,
            };

            console.log(
                "[Resolution Save Payload]",
                JSON.stringify(payload, null, 2)
            );

            const resp = await fetch(url, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            const json = await resp.json().catch(() => ({}));

            if (!resp.ok) {
                setSaveError(
                    json?.message || "Failed to save resolution."
                );
                return;
            }

            // Keep the existing table/button behavior current locally.
            const resolvedValue =
                selectedResolvedOption.title?.en_US || "";

            incident.resolved_incident_type = resolvedValue;

            // Tell ReportSummaries immediately so React redraws the row.
            onResolutionSaved?.(resolvedValue);

            closeResolution();
        } catch (error) {
            console.error(
                "[IncidentResolution] structured save failed:",
                error
            );
            setSaveError("Failed to save resolution.");
        } finally {
            setIsSaving(false);
        }
    };

    const { getAccessTokenSilently } = useAuth0();

    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState("");
    const [clearanceSchema, setClearanceSchema] = useState(null);
    const [isLoadingClearance, setIsLoadingClearance] = useState(false);
    const [clearanceError, setClearanceError] = useState("");
    const [resolutionValues, setResolutionValues] = useState({});



    useEffect(() => {
        setResolutionValues({});
    }, [incident?._id]);


    return (
        <>
            <button
                type="button"
                className={`btn resolution-btn ${incident?.resolved_incident_type
                    ? "btn-primary"
                    : "btn-danger"
                    }`}
                onClick={openResolution}
            >
                {incident?.resolved_incident_type
                    ? "Resolved"
                    : "Add Resolved Type"}
            </button>

            <div
                className={`incident-resolution ${isOpen ? "incident-resolution--open" : ""
                    }`}
                aria-hidden={!isOpen}
            >
                <div className="incident-resolution__header">
                    <h2>Incident Resolution</h2>

                    <button
                        type="button"
                        className="incident-resolution__close"
                        onClick={closeResolution}
                        aria-label="Close resolution"
                    >
                        ×
                    </button>
                </div>

                <div className="incident-resolution__body">

                    {saveError && (
                        <div className="alert alert-danger mt-3">
                            {saveError}
                        </div>
                    )}

                    {isLoadingClearance && <p>Loading resolution actions...</p>}

                    {clearanceError && (
                        <div className="alert alert-danger">{clearanceError}</div>
                    )}

                    {clearanceSchema?.items?.map((item, itemIndex) => (
                        <div
                            key={`${item._id}-${itemIndex}`}
                            className="mb-1"
                        >
                            {/* ------------------------------------------------------------ */}
                            {/* ACTIONS TAKEN SECTION HEADER                                 */}
                            {/* Appears once before the first action/metric field.           */}
                            {/* ------------------------------------------------------------ */}
                            {["number", "integer", "bool"].includes(item.type) &&
                                itemIndex ===
                                clearanceSchema.items.findIndex((schemaItem) =>
                                    ["number", "integer", "bool"].includes(schemaItem.type)
                                ) && (
                                    <div className="incident-resolution__actions-header">
                                        Actions Taken
                                    </div>
                                )}
                            {item.type !== "number" && item.type !== "integer" && (
                                <label className="form-label fw-bold"
                                    style={{
                                        marginBottom: "10px",
                                    }}>
                                    {item.title?.en_US || "Untitled field"}
                                </label>
                            )}
                            {item.type === "text" && (
                                <input
                                    type="text"
                                    className="form-control"
                                    value={resolutionValues[item._id] || ""}
                                    onChange={(e) =>
                                        updateResolutionValue(item._id, e.target.value)
                                    }
                                />
                            )}

                            {(item.type === "number" || item.type === "integer") && (
                                <div
                                    className="d-flex align-items-center"
                                    style={{
                                        justifyContent: "space-between",
                                        gap: 12,
                                        marginBottom: 4,
                                    }}
                                >
                                    <label
                                        className="form-label mb-0"
                                        style={{
                                            width: 220,
                                            flexShrink: 0,
                                            textAlign: "left"
                                        }}
                                    >
                                        {item.title?.en_US || "Untitled field"}
                                    </label>

                                    <input
                                        type="number"
                                        className="form-control flex-shrink-0"
                                        style={{
                                            width: "100px",
                                            height: "38px",
                                            textAlign: "right",
                                        }}
                                        min="0"
                                        step="1"
                                        inputMode="numeric"
                                        value={resolutionValues[item._id] ?? ""}
                                        onKeyDown={(e) => {
                                            if (["e", "E", "+", "-", "."].includes(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        onChange={(e) =>
                                            updateResolutionValue(
                                                item._id,
                                                e.target.value === "" ? "" : Number(e.target.value)
                                            )
                                        }
                                    />
                                </div>
                            )}


                            {item.type === "bool" && (
                                <div className="form-check">
                                    <input
                                        id={`resolution-${item._id}`}
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={resolutionValues[item._id] === true}
                                        onChange={(e) =>
                                            updateResolutionValue(item._id, e.target.checked)
                                        }
                                    />

                                    <label
                                        className="form-check-label"
                                        htmlFor={`resolution-${item._id}`}
                                    >
                                    </label>
                                </div>
                            )}
                            {item.type === "list" && (
                                <select
                                    className="form-select incident-resolution__select"
                                    style={{
                                        marginTop: "4px",
                                    }}
                                    value={resolutionValues[item._id] || ""}
                                    onChange={(e) =>
                                        updateResolutionValue(item._id, e.target.value)
                                    }
                                >
                                   <option value="">
                                        {item.selection_prompt || "Select an option"}
                                    </option>

                                    {(item.items || []).map((option, optionIndex) => (
                                        <option
                                            key={`${option._id}-${optionIndex}`}
                                            value={option._id}
                                        >
                                            {option.title?.en_US || "Untitled option"}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    ))}

                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={closeResolution}
                            disabled={isSaving}
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={saveResolution}
                            disabled={isSaving}
                        >
                            {isSaving ? "Saving..." : "Save Resolution"}
                        </button>
                    </div>
                </div>
            </div>

        </>
    );
};

export default IncidentResolution;